// src/pages/Calculator.jsx
import React, { useState, useMemo } from 'react'
import { computeCalculator } from '../lib/supabase'
import { ShoppingCart, PieChart, Info } from 'lucide-react'

const fmt  = n => n != null ? new Intl.NumberFormat('id-ID').format(Math.round(n)) : '—'
const fmtM = n => n != null ? `Rp ${(n/1_000_000).toFixed(2)} jt` : '—'
const pct  = n => n != null ? `${(n*100).toFixed(1)}%` : '—'
const pct2 = n => n != null ? `${(n*100).toFixed(2)}%` : '—'

function ScoreBar({ sGap, sMos, sDiskon, total }) {
  const scale = total > 0 ? 1/total : 0
  return (
    <div style={{ display:'flex', alignItems:'center', gap:6 }}>
      <div className="score-bar" style={{ width:64, flexShrink:0 }}>
        <div className="score-seg-gap"    style={{ flex: sGap*scale    }}/>
        <div className="score-seg-mos"    style={{ flex: sMos*scale    }}/>
        <div className="score-seg-diskon" style={{ flex: sDiskon*scale }}/>
      </div>
      <span className="mono text-muted" style={{ fontSize:10 }}>{total.toFixed(3)}</span>
    </div>
  )
}

function RankBadge({ rank }) {
  const cls = rank <= 3 ? `rank-badge rank-${rank}` : 'rank-badge'
  return <div className={cls}>{rank}</div>
}

function StatusTag({ status }) {
  const map = {
    'Selaras':   'tag tag-selaras',
    'Masih Gap': 'tag tag-gap',
    'Overweight':'tag tag-over',
  }
  return <span className={map[status] || 'tag tag-hold'}>{status || '—'}</span>
}

function InfoTooltip({ text }) {
  const [show, setShow] = useState(false)
  return (
    <span style={{position:'relative', display:'inline-flex'}}
      onMouseEnter={()=>setShow(true)} onMouseLeave={()=>setShow(false)}>
      <Info size={12} style={{color:'var(--text3)',cursor:'help'}}/>
      {show && (
        <div style={{
          position:'absolute', bottom:'calc(100% + 6px)', left:'50%', transform:'translateX(-50%)',
          background:'var(--bg4)', border:'1px solid var(--border2)', borderRadius:6,
          padding:'8px 10px', width:200, fontSize:11, color:'var(--text2)',
          lineHeight:1.5, zIndex:50, whiteSpace:'normal',
        }}>{text}</div>
      )}
    </span>
  )
}

export default function Calculator({ portfolioData, loading }) {
  const [extraCash, setExtraCash] = useState(0)
  const [rawInput,  setRawInput]  = useState('')

  const result = useMemo(() => {
    if (!portfolioData) return null
    return computeCalculator(portfolioData, extraCash)
  }, [portfolioData, extraCash])

  const handleCashInput = (val) => {
    setRawInput(val)
    const n = parseFloat(val.replace(/[^0-9.]/g, ''))
    setExtraCash(isNaN(n) ? 0 : n)
  }

  if (!portfolioData || loading) return (
    <div className="loading-screen"><div className="loader-ring"/><p>Memuat...</p></div>
  )

  const { allStocks, buyList, budget, newTotal, totalDeploy, sisaCash, utilPct } = result
  const rdnCash  = portfolioData.rdn
  const rdptCash = portfolioData.rdpt

  return (
    <div>
      <div className="calc-grid">

        {/* ── LEFT: Input panel ── */}
        <div className="input-panel">

          <div className="input-card">
            <div className="input-card-title">Budget</div>

            <div className="input-row">
              <label className="input-label">RDPT Cash <span style={{color:'var(--blue)',fontSize:10}}>(termasuk dalam total)</span></label>
              <div className="input-prefix-wrap">
                <span className="input-prefix">Rp</span>
                <input className="input" value={fmt(rdptCash)} readOnly
                  style={{color:'var(--text2)',cursor:'default'}}/>
              </div>
            </div>

            <div className="input-row">
              <label className="input-label">RDN Cash <span style={{color:'var(--green)',fontSize:10}}>(budget beli)</span></label>
              <div className="input-prefix-wrap">
                <span className="input-prefix">Rp</span>
                <input className="input" value={fmt(rdnCash)} readOnly
                  style={{color:'var(--text2)',cursor:'default'}}/>
              </div>
            </div>

            <div className="input-row">
              <label className="input-label">Cash Tambahan</label>
              <div className="input-prefix-wrap">
                <span className="input-prefix">Rp</span>
                <input
                  className="input"
                  placeholder="0"
                  value={rawInput}
                  onChange={e => handleCashInput(e.target.value)}
                />
              </div>
              <div className="input-hint">Contoh: 20000000 untuk Rp 20 jt</div>
            </div>

            <div style={{borderTop:'1px solid var(--border)',paddingTop:14,marginTop:4}}>
              <div style={{display:'flex',justifyContent:'space-between',marginBottom:6}}>
                <span style={{fontSize:11,color:'var(--text3)'}}>Total Budget</span>
                <span className="mono text-blue" style={{fontWeight:500}}>{fmtM(budget)}</span>
              </div>
              <div style={{display:'flex',justifyContent:'space-between'}}>
                <span style={{fontSize:11,color:'var(--text3)'}}>New Total Portfolio</span>
                <span className="mono" style={{fontWeight:500}}>{fmtM(newTotal)}</span>
              </div>
            </div>
          </div>

          {/* Skor legend */}
          <div className="input-card">
            <div className="input-card-title">Bobot Skor Prioritas</div>
            {[
              { label:'Gap (underweight)', w:'40%', color:'var(--blue)',  desc:'Seberapa besar kekurangan alokasi vs target RK' },
              { label:'MoS (safety margin)', w:'35%', color:'var(--green)', desc:'Harga pasar vs Nilai Intrinsik' },
              { label:'Diskon vs Harga RK', w:'25%', color:'var(--amber)', desc:'Seberapa murah vs harga beli RK' },
            ].map(f => (
              <div key={f.label} style={{marginBottom:12}}>
                <div style={{display:'flex',justifyContent:'space-between',marginBottom:4}}>
                  <span style={{fontSize:11,color:'var(--text2)'}}>{f.label}</span>
                  <span style={{fontSize:11,fontWeight:500,color:f.color}}>{f.w}</span>
                </div>
                <div style={{height:4,background:'var(--bg4)',borderRadius:2}}>
                  <div style={{height:4,borderRadius:2,background:f.color,width:f.w}}/>
                </div>
                <div style={{fontSize:10,color:'var(--text3)',marginTop:3}}>{f.desc}</div>
              </div>
            ))}
          </div>

          {/* Ringkasan eksekusi */}
          <div className="input-card">
            <div className="input-card-title">Ringkasan Eksekusi</div>
            <div className="summary-grid">
              <div className="sum-box">
                <div className="sum-box-label">Total Budget</div>
                <div className="sum-box-val accent">{fmtM(budget)}</div>
              </div>
              <div className="sum-box">
                <div className="sum-box-label">Saham Dibeli</div>
                <div className="sum-box-val accent">{buyList.length}</div>
              </div>
              <div className="sum-box">
                <div className="sum-box-label">Diinvestasikan</div>
                <div className="sum-box-val green">{fmtM(totalDeploy)}</div>
              </div>
              <div className="sum-box">
                <div className="sum-box-label">Sisa Cash</div>
                <div className="sum-box-val amber">{fmtM(sisaCash)}</div>
              </div>
            </div>

            {/* Utilisasi bar */}
            <div style={{marginTop:14}}>
              <div style={{display:'flex',justifyContent:'space-between',marginBottom:6,fontSize:11}}>
                <span style={{color:'var(--text3)'}}>Utilisasi Budget</span>
                <span className="mono" style={{color: utilPct > 0.95 ? 'var(--green)' : 'var(--amber)'}}>
                  {pct(utilPct)}
                </span>
              </div>
              <div className="util-bar-track">
                <div className="util-bar-fill" style={{width:`${Math.min(utilPct*100,100)}%`}}/>
              </div>
            </div>
          </div>
        </div>

        {/* ── RIGHT: Tables ── */}
        <div style={{minWidth: 0}}>

          {/* DAFTAR BELI */}
          <div className="section-card" style={{marginBottom:20}}>
            <div className="section-head">
              <div>
                <div className="section-title">
                  <ShoppingCart size={14}/>
                  Daftar Beli — Diurutkan Prioritas Tertinggi
                </div>
                <div className="section-sub">
                  Hanya saham dengan Gap &gt; 0 &amp; Lot &gt; 0 yang muncul
                </div>
              </div>
            </div>

            {buyList.length === 0 ? (
              <div className="empty-state">
                <ShoppingCart size={32}/>
                <p>Tidak ada saham yang perlu dibeli dengan budget saat ini</p>
                <p style={{marginTop:4,fontSize:11}}>Tambah cash di panel kiri</p>
              </div>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th style={{textAlign:'center'}}>Rank</th>
                      <th>Kode</th>
                      <th>Target RK</th>
                      <th>Harga Pasar</th>
                      <th>Harga Modal RK</th>
                      <th>Lot Beli</th>
                      <th>Nilai Beli (Rp)</th>
                      <th>MoS</th>
                      <th>Diskon vs RK</th>
                      <th>
                        Skor &nbsp;
                        <InfoTooltip text="Gap(40%) + MoS(35%) + Diskon(25%) — dinormalisasi 0-1"/>
                      </th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {buyList.map((s, i) => (
                      <tr key={s.code}>
                        <td style={{textAlign:'center'}}>
                          <RankBadge rank={i+1}/>
                        </td>
                        <td><span className="code-chip">{s.code}</span></td>
                        <td className="mono text-muted">{pct(s.rk_alloc)}</td>
                        <td className="mono">{fmt(s.price)}</td>
                        <td className="mono text-muted">{fmt(s.rk_cost)}</td>
                        <td>
                          <span style={{
                            fontWeight:600, color:'var(--green)',
                            background:'var(--green-bg)', padding:'2px 8px',
                            borderRadius:4, fontSize:12,
                          }}>{s.lotBeli} lot</span>
                        </td>
                        <td className="mono">{fmt(s.nilaiBelis)}</td>
                        <td>
                          <span className={s.mos>=0.4?'mos-high':s.mos>=0.2?'mos-mid':'mos-low'}>
                            {pct(s.mos)}
                          </span>
                        </td>
                        <td>
                          <span className={s.diskon>0.1?'text-green':s.diskon>0?'text-amber':'text-dim'}>
                            {pct(s.diskon)}
                          </span>
                        </td>
                        <td>
                          <ScoreBar sGap={s.sGap} sMos={s.sMos} sDiskon={s.sDiskon} total={s.score}/>
                        </td>
                        <td><StatusTag status={s.status}/></td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{background:'var(--bg3)',borderTop:'2px solid var(--border2)'}}>
                      <td colSpan={5} style={{padding:'10px 12px',color:'var(--text3)',fontSize:11}}>
                        TOTAL
                      </td>
                      <td style={{fontWeight:500,color:'var(--green)'}}>
                        {buyList.reduce((a,s)=>a+s.lotBeli,0)} lot
                      </td>
                      <td className="mono" style={{fontWeight:500}}>
                        {fmt(totalDeploy)}
                      </td>
                      <td colSpan={4}></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>

          {/* SEMUA SAHAM */}
          <div className="section-card">
            <div className="section-head">
              <div>
                <div className="section-title"><PieChart size={14}/> Tabel Alokasi Lengkap</div>
                <div className="section-sub">Semua saham · Hijau = underweight · Merah = overweight</div>
              </div>
            </div>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Kode</th>
                    <th>Target RK</th>
                    <th>Harga Pasar</th>
                    <th>Nilai Saat Ini</th>
                    <th>Target Nilai</th>
                    <th>Gap (Rp)</th>
                    <th>Lot Beli</th>
                    <th>Nilai Beli</th>
                    <th>Bobot Baru</th>
                    <th>Δ Target</th>
                  </tr>
                </thead>
                <tbody>
                  {allStocks.map(s => {
                    const isUnder = s.gap > 0
                    const isOver  = s.gap < 0
                    return (
                      <tr key={s.code}>
                        <td><span className="code-chip">{s.code}</span></td>
                        <td className="mono text-muted">{pct(s.rk_alloc)}</td>
                        <td className="mono">{fmt(s.price)}</td>
                        <td className="mono">{fmt(s.mktVal)}</td>
                        <td className="mono">{fmt(s.targetVal)}</td>
                        <td>
                          <span className={isUnder?'text-green':isOver?'text-red':'text-dim'}>
                            {isUnder?'+':''}{fmt(s.gap)}
                          </span>
                        </td>
                        <td>
                          {s.lotBeli > 0
                            ? <span style={{color:'var(--green)',fontWeight:500}}>{s.lotBeli}</span>
                            : <span className="text-dim">—</span>}
                        </td>
                        <td className="mono">
                          {s.nilaiBelis > 0 ? fmt(s.nilaiBelis) : <span className="text-dim">—</span>}
                        </td>
                        <td className="mono" style={{fontSize:11}}>
                          {s.newAlloc != null ? pct(s.newAlloc) : <span className="text-dim">—</span>}
                        </td>
                        <td>
                          {s.rk_alloc > 0 && s.newAlloc != null ? (
                            <span style={{
                              fontSize:11,
                              color: Math.abs(s.newAlloc - s.rk_alloc) < 0.005
                                ? 'var(--green)'
                                : s.newAlloc < s.rk_alloc ? 'var(--amber)' : 'var(--red)',
                            }}>
                              {s.newAlloc >= s.rk_alloc ? '+' : ''}
                              {((s.newAlloc - s.rk_alloc) * 100).toFixed(1)}%
                            </span>
                          ) : (
                            <span className="text-dim">—</span>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>

        </div>
      </div>
    </div>
  )
}
